import React, { useState, useEffect } from "react";
import '../../css/callForward.css';
import { fetchWithDefaults } from '../../config/fetchDefault';
import { useSelector } from 'react-redux';
import Icon from "../../assets/icons/icon";

/**
 * @typedef {Object} ForwardingNumber
 * @property {string} id
 * @property {string} name
 * @property {string} number
 * @property {boolean} active
 */

/**
 * @typedef {Object} ActivateForwardingResponseData
 * @property {string} id
 * @property {string} active
 * @property {boolean} status
 */

/**
 * @typedef {Object} SaveForwardingResponseData
 * @property {string} id
 * @property {number} active
 * @property {boolean} status
 * @property {string} name
 * @property {string} number
 */

/**
 * @callback onChangeCallback
 * @param {string} id
 */

/**
 * @typedef {Object} ToggleSwitchProps
 * @property {string} id
 * @property {boolean} isChecked
 * @property {onChangeCallback} onChange
 */

/**
 * @param {ToggleSwitchProps} props
 * @returns {Element}
 * @constructor
 */
const ToggleSwitch = (props) => {
    const changed = () => {
        if (props.onChange) {
            props.onChange(props.id ?? "");
        }
    };
    return (
      <div>
        <label className="switch">
          <input type="checkbox" id={props.id} checked={props.isChecked} onChange={changed} />
          <span className="slider round"></span>
        </label>
        <span className="sliderDesc">{props.isChecked ? 'Active' : 'Off'}</span>
      </div>
    );
};

function CallForward({data}) {
    const [addNumber, setAddNumber] = useState(false);
    const [editedText, setEditedText] = useState({});
    const [editedIndex, setEditedIndex] = useState(null);
    const [forwardName, setForwardName] = useState(null);
    const [numbers, setNumbers] = useState(/** @type {Array<ForwardingNumber>} */ []);
    const agentId=useSelector((state) => state.agent.userData).agentId;
    const [phoneNumber, setPhoneNumber] = useState('');

    useEffect(() => {
        if(Array.isArray(data) && (data.length > 0) && Array.isArray(data[0])){
            setForwardingNumbers(data[0]);
        }
    },[]);

    const formatPhoneNumber = (value) => {
        // Remove all non-digit characters
        const cleaned = value.replace(/\D/g, '');

        // Limit the number of digits to 10
        const limited = cleaned.slice(0, 10);

        // Format the number
        const match = limited.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
        if (match) {
            const formattedNumber = `${match[1] ? `(${match[1]}` : ''}${match[2] ? `) ${match[2]}` : ''}${match[3] ? `-${match[3]}` : ''}`;
            return formattedNumber;
        }

        return value;
    };
    const handleSetPhoneNumber=(value)=>{

        const formattedValue = formatPhoneNumber(value);
        setPhoneNumber(formattedValue);
    }

    /**
     * @param {*} active
     * @param {string} id
     * @returns {Promise<void>}
     */
    const activateNumber =async (active, id)=>{
        active = coerceBoolean(active);
        try {
            const respData = await sendActivationRequest(id, active);
            const nowActive = coerceBoolean(respData.active);
            const newNums = [...numbers];
            /** @type {Array<string>} */
            const idsToDeactivate = [];
            // For each forwarding number
            //  - If the ID matches the one given to this routine, update its
            //    `active` property
            //  - Else, if we've just activated a new number, and this number
            //    is currently activated, deactivate this number.
            for (const num of newNums) {
                if (num.id === respData.id) {
                    num.active = nowActive;
                } else if (nowActive && num.active) {
                    idsToDeactivate.push(num.id);
                }
            }
            for (const idToDeactivate of idsToDeactivate) {
                const respData = await sendActivationRequest(idToDeactivate, false);
                for (const num of newNums) {
                    if (num.id === respData.id) {
                        num.active = coerceBoolean(respData.active);
                    }
                }
            }
            setNumbers(newNums);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    }
    /**
     * @returns {Promise<Array<*>>}
     */
    const fetchForwardingNumbers = async () => {
        const response = await fetchWithDefaults(`/Call_Forwarding?agent=${agentId}`);
        if (!response.ok) {
            throw new Error("Network response was not ok");
        }
        const respData = await response.json();
        if(Array.isArray(respData) && (respData.length > 0) && Array.isArray(respData[0])){
            return respData[0];
        }
        return [];
    }
    const handleEditClick = (index, text) => {
        setEditedIndex(index);
        //setEditedText({ [index]: text });
        setEditedText({ ...editedText, [index]: text });
    };
    const handleForwardName = (text) => {
        setForwardName(text);
    };

    const handleSaveClick = (index, id) => {
        // Save the edited text or perform any necessary action
        updateName(editedText[index], id);
        setEditedIndex(null);
    };

    const handleInputChange = (e, index) => {
        setEditedText({ ...editedText, [index]: e.target.value });
    };
  
    const addANumber = () => {
        setAddNumber(!addNumber);
    };
    const refreshForwardingNumbers = async () => {
        setForwardingNumbers(
            await fetchForwardingNumbers(),
        );
    }
    const saveNumber =async ()=>{
        try {
            const requestOptions = {
                method: 'POST',
                body: JSON.stringify({ 
                    agent: agentId,
                    number:phoneNumber,
                    name:forwardName
                }) 
            };
          
            const response = await fetchWithDefaults(`/addForwarding`, requestOptions);
            
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            /** @type {SaveForwardingResponseData} */
            const jsonData = await response.json();
            setNumbers([
                ...numbers,
                objectToForwardingNumber(jsonData),
            ]);
            refreshForwardingNumbers();
            setForwardName(null);
            setPhoneNumber(null);
            addANumber();
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    }
    /**
     * @param {string} id
     * @param {boolean} isActive
     * @returns {Promise<ActivateForwardingResponseData>}
     */
    const sendActivationRequest = async (id, isActive) => {
        const requestOptions = {
            method: 'PATCH',
            body: JSON.stringify({
                agent: agentId,
                id,
                active: isActive
                    ? "1"
                    : "0",
            }),
        };
        const response = await fetchWithDefaults('/activateForwarding', requestOptions);
        if (!response.ok) {
            throw new Error("Network response was not ok");
        }
        return await response.json();
    }
    /**
     * @param {Array<*>} nums
     */
    const setForwardingNumbers = (nums) => {
        setNumbers(nums.map(objectToForwardingNumber));
    }
    const deleteNumber =async (id)=>{
        try {
            const requestOptions = {
                method: 'DELETE',
                body: JSON.stringify({ 
                    id: id,
                }) 
            };
          
            const response = await fetchWithDefaults(`/deleteForwarding?id=${id}`, requestOptions);
            
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const jsonData = await response.json();
            const updatedNumbers = numbers.filter((item) => item.id !== id);
            setNumbers(updatedNumbers);
        } 
        catch (error) {
            console.error("Error fetching data:", error);
        }
    }

    /**
     * @param {string} id
     */
    const toggleSwitchChanged = (id) => {
        for (const num of numbers) {
            if (num.id === id) {
                activateNumber(!num.active, id);
                break;
            }
        }
    }

    const updateName = async (friendly, id) => {
        try {
            const requestOptions = {
                method: 'PATCH',
                body: JSON.stringify({ 
                    agent: agentId,
                    id:id,
                    name:friendly
                }) 
            };
          
            const response = await fetchWithDefaults(`/updateForwarding`, requestOptions);
        
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const jsonData = await response.json();
        } 
        catch (error) {
            console.error("Error fetching data:", error);
        }
    };
  return (
    <div className="outerWrapper">
            <span className="outerWrapperTitle">
                <span className="outerWrapperIcon">

                </span>
                Call Forwarding
            </span>
            <div
                className="mainBlockOuterWrapper"
                style={{ justifyContent: "space-around" }}
            >
           {addNumber ? (
            <div className="frontPageQuilityParentBlockWrapper" style={{width:'70%', position:'relative'}}>
                <div className="quilityParentBlockHeader">
                    <div className="quilityParentBlockTitle">Add a New Number</div>
                </div>
                <span className="quilityCloseWindow" onClick={()=>addANumber()}>X</span>
                <div className="quilityParentBlockContent roundedBottom" style={{height: 'fit-content'}}>
                    <div className="quilityNestedBlockWrapper" style={{marginTop: '25px'}}>
                        <div className="quilityNestedBlockHeader">
                            <div className="quilityParentBlockTitle">New Forwarding Number</div>
                        </div>
                        <div className="quilityNestedBlockContent">
                            <div className="quilityDataLine">
                                <div className="quilityDataLineTitle" style={{width: '200px'}}>Name This Number</div>
                                <input 
                                    type='text' 
                                    className="qualifierFieldInput"
                                    value={forwardName}
                                    onChange={(e) => handleForwardName(e.target.value)}
                                />
                            </div>
                            <div className="quilityDataLine">
                                <div className="quilityDataLineTitle" style={{width: '200px'}}>Number To Forward To</div>
                                <input 
                                    type='text' 
                                    className="qualifierFieldInput" 
                                    value={phoneNumber}
                                    onChange={(e) => handleSetPhoneNumber(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="buttonWrapper"><div className="accentButton solidQuilityActiveButton" style={{textAlign:'left'}} onClick={()=>saveNumber()}>Save Number</div></div>
                </div>
                
            </div>  

        ):(
          null
        )}
        <div className={`frontPageQuilityParentBlockWrapper ${addNumber ? 'closeMainBlock' : ''}`} style={{width:'70%'}}>
            <div className="quilityParentBlockHeader">
                <div className="quilityParentBlockTitle">Call Forwarding Numbers</div>

            </div>
            <div className="quilityParentBlockContent" style={{height: 'fit-content'}}>
                {numbers.length > 0 ? numbers.map((item, index) => (
                    <div className="quilityNestedBlockWrapper" key={`forward${index}`} style={{marginTop: '25px'}}>
                        <div className="quilityCloseWindow oulineQuilityDestructiveButton" onClick={() => deleteNumber(item.id)}><span className="fontello" style={{marginTop: '-7px'}}><Icon name={'x-circle-q'} size={34} className="closeIcon" adjust="fontello" /></span></div>
                        <div className="quilityNestedBlockHeader">
                            <div className="quilityParentBlockTitle">
                                <>
                                    {editedText[index] || item.name}
                                    <span
                                    className="hoverIcon"
                                    onClick={() =>
                                        handleEditClick(index, item.name)
                                    }
                                    >
                                    {/*pencil*/}
                                    </span>
                                </>
                            </div>
                        </div>
                        <div className="quilityNestedBlockContent">
                        <div className="innerContentWrapper">
                            <div className="quilityDataLine">
                            <span className="quilityDataLineTitle">Number: </span>
                            {formatPhoneNumber(item.number)}
                            </div>
                        
                            <div className="quilityDataLine">
                            <span className="quilityDataLineTitle">Number Name: </span>
                            {editedIndex === index ? (
                                <input
                                type="text"
                                className="qualifierFieldInput"
                                value={editedText[index]}
                                onChange={(e) => handleInputChange(e, index)}
                                onBlur={() => handleSaveClick(index, item.id)}
                                autoFocus
                                />
                            ) : (
                                <>
                                    <span style={{ marginLeft: "5px" }}>
                                        {editedText[index] || item.name}
                                    </span>
                                    <span
                                        className="hoverIcon"
                                        onClick={() =>
                                        handleEditClick(index, editedText[index])
                                        }
                                    >
                                        {/*pencil*/}
                                    </span>
                                </>
                            )}
                            </div>
                            <div className="quilityDataLine">
                                <ToggleSwitch onChange={toggleSwitchChanged} id={item.id} isChecked={item.active}/>
                            </div>
                        </div>
                    </div>
                </div>
                )) : (
                    <div className='noResults'>You have no forwarding numbers</div>
                )}
            </div>
                <div className="blockFooter">
                    <div
                        className="buttonWrapper"
                        style={{ marginTop: " 5px", marginLeft: "10px" }}
                    >
                        <div className="dataButtonBlock">Numbers: {numbers.length}</div>
                        <div className="actionButtonBlock" style={{width: '150px'}}>
                            <div className="accentButton solidQuilityActiveButton" onClick={()=>addANumber()}>
                            +  New Number
                            </div>
                        </div>
                    </div>
                    
                </div>
            </div>
        </div>
    </div>

  );
}

/**
 * @param {*} value
 * @returns {boolean}
 */
function coerceBoolean(value) {
    if (value === undefined || value === null) {
        return false;
    }
    switch (typeof value) {
        case "string": {
            return (value === "1") || (value.toLowerCase() === "true");
        }
        case "boolean": {
            return value;
        }
        case "number":
        case "bigint": {
            return Boolean(value);
        }
    }
}

/**
 * @param {Object} obj
 * @returns {ForwardingNumber}
 */
function objectToForwardingNumber(obj) {
    return {
        id: obj.id ?? "",
        active: coerceBoolean(obj.active),
        name: obj.name ?? "",
        number: obj.number ?? "",
    }
}

export default CallForward;

